import { CountriesISO3166 } from '@zyno-platform/shared';
import { compact, startCase } from 'lodash';

import type { ICrmContactUpdateAddressInput, ICrmContactUpdatePhoneInput } from '@/openapi-clients-generated/suite';

import { crmPrimaryContactTypeRef } from '@/global.shared';

interface IContact {
    displayName?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    company?: string | null;
    phoneHome?: string | null;
    phoneWork?: string | null;
    phoneCell?: string | null;
    phoneOther?: string | null;
}

export function crmGetContactDisplayName(contact: IContact, enableCompanyName = true) {
    if (contact.displayName) {
        return contact.displayName;
    }
    if (contact.company && crmPrimaryContactTypeRef.value === 'company' && enableCompanyName) {
        return contact.company;
    }
    if (contact.firstName && contact.lastName) {
        return `${contact.firstName} ${contact.lastName}`;
    }
    if (contact.firstName) {
        return contact.firstName;
    }
    if (contact.lastName) {
        return contact.lastName;
    }
    if (contact.company && enableCompanyName) {
        return contact.company;
    }
    return null;
}

export function fixNameCasing(name: string | null | undefined, exceptIfLength?: number) {
    if (!name) {
        return name;
    }
    if (exceptIfLength !== undefined && name.length === exceptIfLength) {
        return name;
    }
    if (name === name.toLowerCase()) {
        return startCase(name);
    }
    if (name === name.toUpperCase()) {
        return startCase(name.toLowerCase());
    }
    return name;
}

/**
 * "Add to contact": append a phone row to an existing contact's set. Returns null when the contact
 * already carries that number, so the caller can say so rather than silently adding a duplicate row.
 * Compared as stored — both sides are E.164 here (CRM phones, and the call/text/fax number the
 * action came from), so a mismatch means a genuinely different number.
 */
export function appendContactPhone(
    phones: ICrmContactUpdatePhoneInput[],
    phone: string,
    defaultPhoneType?: ICrmContactUpdatePhoneInput['type']
): ICrmContactUpdatePhoneInput[] | null {
    if (phones.some(p => p.phone === phone)) return null;
    return [...phones, { phone, type: defaultPhoneType ?? null }];
}

export function formatAddress(address: Pick<ICrmContactUpdateAddressInput, 'street1' | 'street2' | 'city' | 'state' | 'zip' | 'country'>) {
    const cs = compact([fixNameCasing(address.city), fixNameCasing(address.state, 2)]).join(', ');
    const csz = compact([cs, address.zip]).join(' ');
    const country = address.country && address.country !== 'US' ? CountriesISO3166[address.country as keyof typeof CountriesISO3166] : null;
    const components = compact([fixNameCasing(address.street1), fixNameCasing(address.street2), csz, country]);
    return components.join('\n');
}
